import { useCallback, useEffect, useRef, useState } from 'react'
import { playbackService } from '@/services/playback'

const PLAYBACK_TIMEFFRAMES = new Set(['15m', '1h', '4h'])
const DEFAULT_SPEED = 2
const TICK_MS = 1000

/**
 * Playback controller.
 *
 * In normal mode it simply forwards the live controller returned by
 * `useChartData`.  When playback is entered, it loads 90 days of pre-calculated
 * per-bar snapshots from the backend and replays them candle by candle.
 */
export function usePlaybackController(symbol, timeframe, liveController) {
  const [isPlayback, setIsPlayback] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [bars, setBars] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [visibleBars, setVisibleBars] = useState([])
  const [speed, setSpeed] = useState(DEFAULT_SPEED)
  const [tick, setTick] = useState(0)

  const subscribersRef = useRef(new Set())
  const timerRef = useRef(null)
  const visibleRef = useRef([])

  const notify = useCallback(() => {
    setTick(t => t + 1)
    subscribersRef.current.forEach(cb => cb())
  }, [])

  const isSupported = PLAYBACK_TIMEFFRAMES.has(timeframe)

  const enterPlayback = useCallback(async () => {
    if (!isSupported) {
      setError(`El playback solo está disponible para ${[...PLAYBACK_TIMEFFRAMES].join('/')}`)
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const toTime = Math.floor(Date.now() / 1000)
      const fromTime = toTime - 90 * 24 * 60 * 60
      const res = await playbackService.getPlaybackData({
        symbol,
        timeframe,
        from_time: fromTime,
        to_time: toTime,
      })
      const data = res.data
      const loadedBars = data?.bars || []
      if (loadedBars.length === 0) {
        setError('No se encontraron datos de playback')
        return
      }
      setBars(loadedBars)
      setCurrentIndex(loadedBars.length - 1)
      setIsPlayback(true)
      notify()
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Error cargando playback')
    } finally {
      setIsLoading(false)
    }
  }, [symbol, timeframe, notify])

  const exitPlayback = useCallback(() => {
    setIsPlayback(false)
    setIsPlaying(false)
    setBars([])
    setCurrentIndex(0)
    setError(null)
    visibleRef.current = []
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    notify()
  }, [notify])

  useEffect(() => {
    if (!isPlayback) {
      visibleRef.current = liveController.data || []
      setVisibleBars(liveController.data || [])
      return
    }
    const visible = bars.slice(0, currentIndex + 1)
    visibleRef.current = visible
    setVisibleBars(visible)
    notify()
  }, [isPlayback, currentIndex, bars, liveController.data, notify])

  useEffect(() => {
    if (!isPlayback || !isPlaying) return
    timerRef.current = setInterval(() => {
      setCurrentIndex(prev => {
        if (prev >= bars.length - 1) {
          setIsPlaying(false)
          return prev
        }
        return prev + 1
      })
    }, Math.max(100, TICK_MS / speed))
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [isPlayback, isPlaying, bars.length, speed])

  const play = useCallback(() => setIsPlaying(true), [])
  const pause = useCallback(() => setIsPlaying(false), [])
  const stepForward = useCallback((n = 1) => {
    setCurrentIndex(prev => Math.min(bars.length - 1, prev + n))
  }, [bars.length])
  const stepBackward = useCallback((n = 1) => {
    setCurrentIndex(prev => Math.max(0, prev - n))
  }, [])
  const seek = useCallback((index) => {
    setCurrentIndex(Math.max(0, Math.min(bars.length - 1, index)))
  }, [bars.length])

  const subscribe = useCallback((cb) => {
    subscribersRef.current.add(cb)
    return () => subscribersRef.current.delete(cb)
  }, [])

  if (!isPlayback) {
    return {
      isPlayback,
      isSupported,
      enterPlayback,
      exitPlayback,
      data: liveController.data,
      dataRef: liveController.dataRef,
      subscribe: liveController.subscribe,
      tick: liveController.tick,
      isLoading: isLoading || liveController.isLoading,
      error: error || liveController.error,
    }
  }

  return {
    isPlayback,
    enterPlayback,
    exitPlayback,
    data: visibleBars,
    dataRef: visibleRef,
    subscribe,
    tick,
    isPlaying,
    play,
    pause,
    currentIndex,
    totalBars: bars.length,
    stepForward,
    stepBackward,
    seek,
    speed,
    setSpeed,
    isLoading,
    error,
  }
}
